import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const DEFAULTS = {
  y: 32, // slide-up distance in px; set to 0 for an opacity-only fade
  opacity: 0,
  duration: 0.7,
  ease: 'power3.out',
  stagger: 0.12,
  start: 'top 85%',
  delay: 0,
}

// Per-element overrides read off data attributes, e.g.
//   data-fade-duration="1.5" data-fade-start="top 65%" data-fade-ease="power1.out" data-fade-y="0"
// Any element carrying at least one of these opts out of batching and reveals on
// its own trigger with the merged config; everything else batches as before.
const NUMERIC = ['y', 'opacity', 'duration', 'stagger', 'delay']
const STRING = ['ease', 'start']

/**
 * Fade (and optionally slide up) elements into view on scroll, the first time
 * each enters. Elements that cross the trigger together — a row of cards, say —
 * are batched so they stagger as a group; ones far apart animate on their own as
 * they arrive.
 *
 * Per element, any `data-fade-*` attribute overrides the matching option for
 * that element only (falling back to `options`/DEFAULTS when absent):
 *   data-fade-start, data-fade-duration, data-fade-ease, data-fade-y,
 *   data-fade-opacity, data-fade-delay
 *
 * Pass `y: 0` for an opacity-only fade. That matters for Swiper slides: writing a
 * transform on a slide would clobber Swiper's own translate, so those must never
 * be given a `y`.
 *
 * Respects `prefers-reduced-motion`: elements are simply shown, no movement.
 *
 * @param {string|Element|Element[]|NodeList} targets - elements (or a selector)
 * @param {object} [options] - defaults for this call: { y, opacity, duration, ease, stagger, start, delay }
 */
export function createScrollFade(targets, options = {}) {
  const config = { ...DEFAULTS, ...options }
  const elements = gsap.utils.toArray(targets)
  if (!elements.length) return

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduceMotion) {
    gsap.set(elements, config.y ? { opacity: 1, y: 0 } : { opacity: 1 })
    return
  }

  // Split: elements with data-fade-* overrides reveal solo, the rest batch.
  const solo = []
  const batched = []
  for (const el of elements) {
    ;(Object.keys(readOverrides(el)).length ? solo : batched).push(el)
  }

  if (batched.length) {
    gsap.set(batched, hiddenState(config))
    ScrollTrigger.batch(batched, {
      start: config.start,
      once: true,
      onEnter: (batch) => gsap.to(batch, { ...shownVars(config), stagger: config.stagger }),
    })
  }

  solo.forEach((el) => {
    const cfg = { ...config, ...readOverrides(el) }
    gsap.set(el, hiddenState(cfg))
    ScrollTrigger.create({
      trigger: el,
      start: cfg.start,
      once: true,
      onEnter: () => gsap.to(el, shownVars(cfg)),
    })
  })

  // Reveals can shift once fonts load (headings re-wrap); refresh so triggers
  // measure against the final positions.
  if (document.fonts) document.fonts.ready.then(() => ScrollTrigger.refresh())
}

function readOverrides(el) {
  const out = {}
  for (const key of NUMERIC) {
    const v = el.dataset[`fade${cap(key)}`]
    if (v != null) out[key] = parseFloat(v)
  }
  for (const key of STRING) {
    const v = el.dataset[`fade${cap(key)}`]
    if (v != null) out[key] = v
  }
  return out
}

function hiddenState(cfg) {
  const hidden = { opacity: cfg.opacity }
  if (cfg.y) hidden.y = cfg.y
  return hidden
}

function shownVars(cfg) {
  const vars = { opacity: 1, duration: cfg.duration, ease: cfg.ease, overwrite: 'auto' }
  if (cfg.y) vars.y = 0
  if (cfg.delay) vars.delay = cfg.delay
  return vars
}

function cap(s) {
  return s[0].toUpperCase() + s.slice(1)
}
